import { useEffect } from "react";
import {
  Upload,
  Typography,
  Flex,
  Popconfirm,
  Image,
  Progress,
  Space,
  Checkbox,
} from "antd";
import PrimaryButton from "../CustomButton/PrimaryButton";
import CustomButton from "../CustomButton/Button";
import DangerButton from "../CustomButton/DangerButton";
import CustomModal from "../CustomModal/CustomModal";
import {
  BTN_TXT,
  DESC_TXT,
  ROLE_MODAL_WIDTHS,
  REASON_MODAL_CONFIG,
} from "../../constants/constants";
import { useDocUpload } from "../../hooks/useDocUpload";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import ReasonForm from "../CustomForms/ReasonForm";
import { useSelector } from "react-redux";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import "./DocUploadCard.scss";

const { Text } = Typography;

const ApproveEntDocUploadCard = ({
  fileTitle,
  fileType,
  fileData,
  entId,
  onFileChange,
  isReview,
  fileKey,
  mouChecked,
  setMouChecked,
}) => {
  const { rejectDocError, verifyDocLoading, rejectDocLoading } = useSelector(
    (state) => state?.enqMngt,
  );
  const uploadPath = `enterprise/${entId}`;
  const {
    file,
    setFile,
    uploading,
    progress,
    previewOpen,
    setPreviewOpen,
    deleteFile,
    uploadProps,
    sampleDownloaded,
    handleSampleDownload,
    modal,
    setModal,
    handleVerifyDoc,
    handleReasonSubmit,
  } = useDocUpload({
    onFileChange,
    uploadPath,
    fileType,
    fileTitle,
    entId,
    fileData,
    fileKey,
  });

  useEffect(() => {
    if (
      fileData?.fileUrl &&
      !fileData?.rejected &&
      (fileData.fileUrl !== file.fileUrl || fileData.fileName !== file.fileName)
    ) {
      setFile(fileData);
      onFileChange?.({
        title: fileTitle,
        type: fileType,
        fileUrl: fileData?.fileUrl,
        fileName: fileData?.fileName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData]);

  return (
    <div className="doc-upload-card">
      <Flex align="center" justify="space-between" gap={20}>
        <Space direction="vertical" size="small">
          <Text className="file-title">
            {fileTitle}{" "}
            {fileData?.isVerified && (
              <CheckCircleFilled style={{ color: "#00874F" }} />
            )}
            {fileData?.rejected && (
              <CloseCircleFilled style={{ color: "red" }} />
            )}
          </Text>
          {entId && fileData?.rejected ? (
            <Text className="file-desc">{`${DESC_TXT.UPLOAD_DOC_DESC} ${fileType}`}</Text>
          ) : file?.fileName ? (
            <Text className="file-desc">{file?.fileName}</Text>
          ) : (
            <Text className="file-desc">{`${DESC_TXT.UPLOAD_DOC_DESC} ${fileType}`}</Text>
          )}
        </Space>

        {fileKey === "mou" && !file?.fileName && (
          <Flex vertical align="center" gap={5}>
            <Checkbox
              checked={mouChecked === true}
              onChange={() => setMouChecked(true)}
            >
              Yes
            </Checkbox>
            <Checkbox
              checked={mouChecked === false}
              onChange={() => setMouChecked(false)}
            >
              No
            </Checkbox>
          </Flex>
        )}

        {file.fileName ? (
          entId && fileData?.rejected ? (
            <Upload {...uploadProps} showUploadList={false}>
              <PrimaryButton>{BTN_TXT.REUPLOAD}</PrimaryButton>
            </Upload>
          ) : (
            <Flex align="center" gap={20}>
              <CustomButton
                variant="outlined"
                color="primary"
                style={{ fontWeight: "500" }}
                onClick={() => setPreviewOpen(true)}
              >
                {BTN_TXT.VIEW}
              </CustomButton>
              {!isReview && (
                <Popconfirm
                  title="Are you sure you want to delete this file?"
                  okText="Yes"
                  cancelText="No"
                  placement="topRight"
                  onConfirm={() =>
                    deleteFile(file.fileUrl, () =>
                      onFileChange({
                        title: fileTitle,
                        type: fileType,
                        fileUrl: null,
                        fileName: null,
                      }),
                    )
                  }
                >
                  <DangerButton variant="outlined">
                    {BTN_TXT.DELETE}
                  </DangerButton>
                </Popconfirm>
              )}
            </Flex>
          )
        ) : fileType === "XLSX" && !sampleDownloaded ? (
          <PrimaryButton onClick={() => handleSampleDownload(fileKey)}>
            {BTN_TXT.DWNLD_FILE}
          </PrimaryButton>
        ) : (
          <Upload
            {...uploadProps}
            disabled={fileKey === "mou" && mouChecked !== true}
          >
            <PrimaryButton disabled={fileKey === "mou" && mouChecked !== true}>
              {BTN_TXT.UPLOAD}
            </PrimaryButton>
          </Upload>
        )}
      </Flex>

      {/* Progress bar */}
      {uploading && <Progress percent={progress} />}

      {/* Preview Modal */}
      {previewOpen && (
        <CustomModal
          title={`Viewing ${fileTitle} (${file.fileName})`}
          width={ROLE_MODAL_WIDTHS}
          open
          centered
          closeIcon
          onCancel={() => setPreviewOpen(false)}
          content={
            <>
              {file.fileUrl ? (
                file.fileUrl.endsWith(".pdf") ? (
                  <iframe
                    src={file.fileUrl}
                    title="PDF Preview"
                    style={{ width: "100%", height: "60vh", border: "none" }}
                  />
                ) : (
                  <Image
                    src={file.fileUrl}
                    alt={file.fileName}
                    preview={false}
                    style={{ maxHeight: "100%", maxWidth: "100%" }}
                  />
                )
              ) : (
                <Text>No preview available</Text>
              )}
              {isReview && (
                <Flex
                  gap={10}
                  justify="space-between"
                  style={{
                    marginTop: 20,
                    position: "sticky",
                    padding: 10,
                  }}
                >
                  <div>
                    {rejectDocError && <ErrorAlert error={rejectDocError} />}
                  </div>
                  <Flex gap={10}>
                    <DangerButton
                      isSolid={true}
                      disabled={fileData?.rejected}
                      onClick={() => setModal(true)}
                    >
                      {BTN_TXT.REJECT}
                    </DangerButton>
                    <PrimaryButton
                      loading={verifyDocLoading}
                      disabled={fileData?.isVerified}
                      onClick={handleVerifyDoc}
                    >
                      {BTN_TXT.VERIFY}
                    </PrimaryButton>
                  </Flex>
                </Flex>
              )}
              {modal && (
                <CustomModal
                  title={REASON_MODAL_CONFIG["reject"]?.title}
                  open
                  centered
                  content={
                    <ReasonForm
                      label={REASON_MODAL_CONFIG["reject"]?.label}
                      placeholder={`Write a valid reason`}
                      btnName={REASON_MODAL_CONFIG["reject"]?.btnName}
                      btnType="danger"
                      submitLoading={rejectDocLoading}
                      submitError={rejectDocError}
                      onClose={() => setModal(false)}
                      handleOnFormSubmit={handleReasonSubmit}
                    />
                  }
                />
              )}
            </>
          }
        />
      )}
    </div>
  );
};

export default ApproveEntDocUploadCard;
